/**
 * AccountMenu — the shell account avatar + dropdown (handoff element E).
 *
 * Avatar shows the signed-in user's initial. The dropdown carries the account
 * state (username), a dark-mode toggle (theme system), and Log out / Log in.
 * All auth behaviour is unchanged — it reuses useAccess + the same logout call
 * the shell used before.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { LogIn, LogOut, Moon, Sun } from 'lucide-react';
import { useAccess } from '@/lib/access';
import { isDark, toggleMode, subscribeTheme } from '@/lib/theme';

export function AccountMenu() {
  const navigate = useNavigate();
  const { userId, username, authLoading, refresh: refreshAuth } = useAccess();
  const isAuthed = !!userId;

  const [open, setOpen] = useState(false);
  const [dark, setDark] = useState<boolean>(isDark());
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => subscribeTheme((s) => setDark(s.mode === 'dark')), []);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  async function handleLogout() {
    try {
      await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    } catch {
      /* swallow — the server clears the cookie regardless */
    }
    setOpen(false);
    refreshAuth();
    navigate('/login');
  }

  const initial = (username || userId || '?').trim().charAt(0).toUpperCase() || '?';

  return (
    <div ref={wrapRef} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        className="flex h-8 w-8 items-center justify-center rounded-full text-[12px] font-semibold text-white transition-opacity hover:opacity-90"
        style={{ background: 'linear-gradient(135deg, #334155, #64748B)' }}
      >
        {isAuthed ? initial : '?'}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-[calc(100%+8px)] z-120 w-56 overflow-hidden rounded-xl border border-border bg-card py-1.5 shadow-2xl"
        >
          <div className="px-3 py-2 text-[12px] text-muted-foreground">
            {authLoading ? (
              'Checking session…'
            ) : isAuthed ? (
              <>Signed in as <span className="font-semibold text-foreground">{username || userId}</span></>
            ) : (
              'Not signed in'
            )}
          </div>

          <div className="my-1 h-px bg-border" />

          <button
            type="button"
            role="menuitem"
            onClick={() => toggleMode()}
            className="flex w-full items-center gap-2.5 px-3 py-2 text-[13px] text-foreground transition-colors hover:bg-muted"
          >
            {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            <span>Dark mode</span>
            <span
              className={`ml-auto flex h-[18px] w-[32px] items-center rounded-full px-[2px] transition-colors ${
                dark ? 'justify-end bg-primary' : 'justify-start bg-muted-foreground/40'
              }`}
              aria-hidden="true"
            >
              <span className="h-[14px] w-[14px] rounded-full bg-white shadow-sm" />
            </span>
          </button>

          <div className="my-1 h-px bg-border" />

          {isAuthed ? (
            <button
              type="button"
              role="menuitem"
              onClick={handleLogout}
              className="flex w-full items-center gap-2.5 px-3 py-2 text-[13px] text-foreground transition-colors hover:bg-muted"
            >
              <LogOut className="h-4 w-4" />
              <span>Log out</span>
            </button>
          ) : (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                navigate('/login');
              }}
              className="flex w-full items-center gap-2.5 px-3 py-2 text-[13px] text-foreground transition-colors hover:bg-muted"
            >
              <LogIn className="h-4 w-4" />
              <span>Log in</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
